import CloseIcon from '@mui/icons-material/Close';
import { BlueprintDownloadDialog } from '@/common/components/blueprints/BlueprintDownloadDialog';
import { BlueprintAudioPreview } from '@/common/components/blueprints/BlueprintAudioPreview';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Link,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TagIcon from '@mui/icons-material/Tag';
import SortIcon from '@mui/icons-material/Sort';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FolderIcon from '@mui/icons-material/FolderOpenOutlined';
import DragIcon from '@mui/icons-material/DragIndicator';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import {
  useBlueprintsQuery,
  useBlueprintCommunityStatusQuery,
  useBlueprintTagsQuery,
  useBlueprintFavoriteSetMutation,
  type BlueprintFieldsFragment,
  type BlueprintKind,
} from '@/generated/graphql';
import FadeContent from '@/common/components/reactBits/FadeContent';
import SpotlightCard from '@/common/components/reactBits/SpotlightCard';
import { PageBanner } from '@/common/components/ui/PageBanner';
import { useModeTokens } from '@/common/components/ui/useModeTokens';
import { BlueprintArtwork } from '@/common/components/blueprints/BlueprintArtwork';
import { useBlueprintLibrary } from '@/common/components/blueprints/useBlueprintLibrary';
import '@/common/components/blueprints/blueprints.css';
const blueprintTabs = [
  { value: 'explore', label: 'Explore', loadingMessage: 'Loading blueprints…' },
  {
    value: 'favorites',
    label: 'Favorites',
    loadingMessage: 'Loading favorites…',
  },
  {
    value: 'downloaded',
    label: 'Downloaded',
    loadingMessage: 'Loading downloaded blueprints…',
  },
  {
    value: 'outdated',
    label: 'Outdated',
    loadingMessage: 'Checking blueprint updates…',
  },
  {
    value: 'mine',
    label: 'My uploads',
    loadingMessage: 'Loading your uploads…',
  },
];

export default function BlueprintsPage() {
  const themeTokens = useModeTokens();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [creator, setCreator] = useState<{ id: string; name: string } | null>(
    searchParams.get('creator')
      ? {
          id: searchParams.get('creator')!,
          name: searchParams.get('creatorName') || 'Creator',
        }
      : null
  );
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [sort, setSort] = useState('newest');
  const [section, setSection] = useState('explore');
  const localSection = section === 'downloaded' || section === 'outdated';
  const [stockOnly, setStockOnly] = useState(false);
  const [blueprintType, setBlueprintType] = useState<BlueprintKind | ''>('');
  const [offset, setOffset] = useState(0);
  const hasFilters = !!(
    query ||
    tags.length ||
    blueprintType ||
    stockOnly ||
    creator
  );
  const [actionError, setActionError] = useState('');
  const community = useBlueprintCommunityStatusQuery({
    fetchPolicy: 'network-only',
  });
  const communityStatus = community.data?.blueprintCommunityStatus;
  const library = useBlueprintLibrary();
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);
  const {
    data: remoteData,
    loading: remoteLoading,
    error: remoteError,
    refetch,
  } = useBlueprintsQuery({
    variables: {
      filter: {
        ...(creator ? { creatorId: creator.id } : {}),
        blueprintType: blueprintType || undefined,
        search: query,
        tags,
        sort,
        stockOnly,
        mine: section === 'mine',
        favorites: section === 'favorites',
        ...(localSection ? { ids: Object.keys(library.downloaded) } : {}),
        offset,
        limit: 24,
      },
    },
    skip: localSection,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  useEffect(() => {
    if (localSection) return;
    const refresh = () => {
      void refetch().catch(() => {});
    };
    window.addEventListener('blueprint-library-changed', refresh);
    return () =>
      window.removeEventListener('blueprint-library-changed', refresh);
  }, [localSection, refetch]);
  const tagQuery = useBlueprintTagsQuery({
    variables: { search: '' },
    fetchPolicy: 'cache-and-network',
  });
  const [favorite, favoriteState] = useBlueprintFavoriteSetMutation();
  const localItems = Object.values(library.downloaded)
    .flatMap((record) => (record.blueprint ? [record.blueprint] : []))
    .filter(
      (blueprint) =>
        (section !== 'outdated' || library.outdated.includes(blueprint._id)) &&
        (!creator || blueprint.creatorId === creator.id) &&
        (!blueprintType || blueprint.blueprintType === blueprintType) &&
        (!stockOnly || blueprint.stockOnly) &&
        tags.every((tag) => blueprint.tags.includes(tag)) &&
        `${blueprint.title} ${blueprint.description} ${blueprint.pluginName} ${blueprint.tags.join(' ')}`
          .toLowerCase()
          .includes(query.toLowerCase())
    )
    .sort((a, b) =>
      sort === 'downloads'
        ? b.downloadCount - a.downloadCount
        : sort === 'favorites'
          ? b.favoriteCount - a.favoriteCount
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  const data = localSection
    ? {
        blueprints: {
          total: localItems.length,
          items: localItems.slice(offset, offset + 24),
        },
      }
    : remoteData;
  const loading = localSection ? library.loading : remoteLoading;
  const loadingMessage = blueprintTabs.find(
    (tab) => tab.value === section
  )!.loadingMessage;
  const error = localSection ? undefined : remoteError;
  const reload = () => {
    if (localSection)
      void library.refresh().catch((e) => library.setError(String(e)));
    else void refetch();
    void tagQuery.refetch();
  };
  const toggleFavorite = async (blueprint: BlueprintFieldsFragment) => {
    setActionError('');
    try {
      await favorite({
        variables: { id: blueprint._id, favorite: !blueprint.isFavorite },
      });
      reload();
    } catch (e) {
      setActionError(String(e));
    }
  };
  const showCreator = (blueprint: BlueprintFieldsFragment) => {
    setCreator({ id: blueprint.creatorId, name: blueprint.creatorName });
    setSection('explore');
    setSearch('');
    setQuery('');
    setTags([]);
    setStockOnly(false);
    setBlueprintType('');
    setSort('newest');
    setOffset(0);
  };
  const creatorLink = (blueprint: BlueprintFieldsFragment) => (
    <Link
      component="button"
      type="button"
      variant="inherit"
      underline="hover"
      disabled={!blueprint.creatorId}
      onClick={() => showCreator(blueprint)}
      aria-label={`View uploads by ${blueprint.creatorName}`}
      sx={{ verticalAlign: 'baseline', fontWeight: 600, textAlign: 'left' }}
    >
      {blueprint.creatorName}
    </Link>
  );
  const openUpload = (blueprint?: BlueprintFieldsFragment) => {
    navigate(
      blueprint
        ? `/blueprints/${encodeURIComponent(blueprint._id)}/edit`
        : '/blueprints/upload'
    );
  };
  const actions = (
    blueprint: BlueprintFieldsFragment,
    prominent = false,
    card = false
  ) => {
    const local = library.downloaded[blueprint._id];
    const busy = library.busy.includes(blueprint._id);
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={card ? { '& > :last-child': { marginLeft: 'auto' } } : undefined}
      >
        {local && !library.outdated.includes(blueprint._id) ? (
          <>
            <Button
              variant="contained"
              size="small"
              startIcon={<DragIcon />}
              draggable
              onDragStart={(e) => {
                e.preventDefault();
                library.drag(blueprint._id);
              }}
            >
              Drag into DAW
            </Button>
            <Tooltip title="Show in folder">
              <IconButton
                aria-label={`Show ${blueprint.title} in folder`}
                onClick={() => void library.reveal(blueprint._id)}
              >
                <FolderIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button
            size="small"
            variant={prominent ? 'contained' : 'outlined'}
            sx={{ minHeight: 32, px: 1.5 }}
            startIcon={<DownloadIcon />}
            disabled={busy || !blueprint.sharingEnabled}
            onClick={() => void library.download(blueprint)}
          >
            {busy
              ? 'Downloading…'
              : blueprint.sharingEnabled
                ? library.outdated.includes(blueprint._id)
                  ? `Update to v${library.updates[blueprint._id]?.version}`
                  : 'Download'
                : 'Unavailable'}
          </Button>
        )}
        <Tooltip
          title={blueprint.isFavorite ? 'Remove favorite' : 'Save favorite'}
        >
          <span>
            <IconButton
              aria-label={
                blueprint.isFavorite
                  ? `Unfavorite ${blueprint.title}`
                  : `Favorite ${blueprint.title}`
              }
              disabled={favoriteState.loading || !blueprint.sharingEnabled}
              onClick={() => void toggleFavorite(blueprint)}
              color={blueprint.isFavorite ? 'primary' : 'default'}
            >
              {blueprint.isFavorite ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  };
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ maxWidth: 1520, px: { xs: 1, sm: 2 }, pb: 5 }}
    >
      <FadeContent playOnMount duration={550}>
        <PageBanner variant="hero">
          <Box>
            <Typography variant="overline" color="text.secondary">
              COMMUNITY / SOUND DESIGN
            </Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Blueprints
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Good sounds are better shared.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openUpload()}
            disabled={communityStatus?.blocked}
          >
            Share a blueprint
          </Button>
        </PageBanner>
      </FadeContent>
      <FadeContent playOnMount delay={80}>
        <Box
          className="blueprint-library-intro"
          sx={{
            borderColor: themeTokens.borders.hairline,
            backgroundColor: themeTokens.surfaces.screen,
            boxShadow: themeTokens.shadows.controlInset,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} mb={2}>
              <Chip label="COMMUNITY BUILT" size="small" variant="outlined" />
              <Chip
                label="PRESETS + FX CHAINS"
                size="small"
                variant="outlined"
              />
            </Stack>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 25, md: 36 },
                fontWeight: 500,
                letterSpacing: '-.04em',
                maxWidth: 550,
              }}
            >
              Someone’s happy accident.
              <br />
              Your next signature sound.
            </Typography>
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ mt: 1.5, maxWidth: 500 }}
            >
              An open source library of community-driven tools. Share your own
              creations, build on others’ work, and help the next producer find
              their sound.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              mt={2.5}
              sx={{ color: 'text.secondary' }}
            >
              <Typography variant="caption">01 / FIND A SOUND</Typography>
              <Typography variant="caption">02 / DOWNLOAD</Typography>
              <Typography variant="caption">03 / CREATE</Typography>
            </Stack>
          </Box>
          <Box className="blueprint-library-intro__art" aria-hidden="true">
            <BlueprintArtwork title="Studio Vault" large />
          </Box>
        </Box>
      </FadeContent>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{
          mt: 2,
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Share your craft. Respect other creators’ work.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Link
            href="/community-guidelines"
            target="_blank"
            rel="noreferrer"
            variant="body2"
          >
            Sharing & copyright
          </Link>
          {communityStatus?.canModerate && (
            <Link href="/admin" variant="body2">
              Moderation
            </Link>
          )}
        </Stack>
      </Stack>
      {communityStatus?.blocked && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your account is blacklisted from community sharing.{' '}
          {communityStatus.reason}{' '}
          <Link
            href="/community-guidelines#appeals"
            target="_blank"
            rel="noreferrer"
          >
            Appeal this decision
          </Link>
        </Alert>
      )}
      {section === 'mine' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Uploads need your explicit permission to be shared under CC0. Open a
          blueprint and choose “Review sharing terms” if it needs your consent.
          Withheld blueprints remain here so you can review the reason or
          appeal.
        </Alert>
      )}
      <Box
        component="nav"
        aria-label="Blueprint sections and actions"
        sx={{
          mt: 3,
          mb: 2,
          px: 1.5,
          position: 'sticky',
          top: { xs: 56, md: 0 },
          zIndex: 4,
          bgcolor: themeTokens.surfaces.canvas,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: themeTokens.borders.hairline,
          boxShadow: themeTokens.shadows.controlInset,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Tabs
            value={section}
            onChange={(_, value) => {
              setSection(value);
              setOffset(0);
            }}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ minWidth: 0, flex: 1 }}
          >
            {blueprintTabs.map(({ value, label }) => (
              <Tab
                key={value}
                value={value}
                aria-label={label}
                disabled={
                  ['downloaded', 'outdated'].includes(value) &&
                  !library.isElectron
                }
                label={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {label}
                    {value === 'outdated'
                      ? library.updateError
                        ? ' (?)'
                        : ` (${library.outdated.length})`
                      : ''}
                    {loading && section === value && (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        color="inherit"
                        aria-label={loadingMessage}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
          <Button
            aria-label="Share a blueprint"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openUpload()}
            disabled={communityStatus?.blocked}
            size="small"
            sx={{
              flexShrink: 0,
              minWidth: { xs: 40, sm: 'auto' },
              px: { xs: 1, sm: 1.5 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box
              component="span"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
            >
              Share blueprint
            </Box>
          </Button>
        </Stack>
      </Box>
      {localSection && library.updateError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button onClick={reload}>Retry</Button>}
        >
          {library.updateError}
        </Alert>
      )}
      {section === 'outdated' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Downloaded Blueprints with a newer release. Keeping an older copy
          alongside the latest does not mark a blueprint outdated.
        </Typography>
      )}
      {creator && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing uploads by
          </Typography>
          <Chip
            label={creator.name}
            color="primary"
            variant="outlined"
            onDelete={() => {
              setCreator(null);
              setOffset(0);
            }}
            deleteIcon={<CloseIcon aria-label="Clear uploader filter" />}
          />
        </Stack>
      )}
      <Box
        component="section"
        aria-label="Blueprint library filters"
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'studio.surfaces.screen',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'minmax(180px, 1fr) 130px minmax(160px, 240px) 170px',
            },
            gap: 1.25,
            p: 1.5,
            '& .MuiOutlinedInput-root': { minHeight: 44, borderRadius: 2 },
          }}
        >
          <TextField
            size="small"
            placeholder="Search blueprints, sounds, inspiration…"
            inputProps={{ 'aria-label': 'Search blueprints' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'primary.main', fontSize: 21 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <TextField
            select
            size="small"
            value={blueprintType}
            SelectProps={{
              displayEmpty: true,
              inputProps: { 'aria-label': 'Blueprint type' },
            }}
            onChange={(e) => {
              const value = e.target.value as BlueprintKind | '';
              setBlueprintType(value);
              if (value === 'preset') setStockOnly(false);
              setOffset(0);
            }}
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="fxChain">FX chains</MenuItem>
            <MenuItem value="preset">Presets</MenuItem>
          </TextField>
          <Autocomplete
            multiple
            size="small"
            limitTags={1}
            options={tagQuery.data?.blueprintTags || []}
            value={tags}
            onChange={(_, value) => {
              setTags(value);
              setOffset(0);
            }}
            sx={{ minWidth: 0 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={tags.length ? '' : 'Filter by tags'}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Filter by tags',
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ ml: 0.75 }}>
                        <TagIcon
                          sx={{ fontSize: 18, color: 'text.secondary' }}
                        />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            select
            size="small"
            value={sort}
            SelectProps={{ inputProps: { 'aria-label': 'Sort blueprints' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SortIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setSort(e.target.value);
              setOffset(0);
            }}
          >
            <MenuItem value="newest">Newest first</MenuItem>
            <MenuItem value="downloads">Most downloaded</MenuItem>
            <MenuItem value="favorites">Most favorited</MenuItem>
          </TextField>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            gap: 1,
            flexWrap: 'wrap',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            role="status"
            aria-live="polite"
          >
            {loading ? (
              loadingMessage
            ) : (
              <>
                <Box
                  component="span"
                  sx={{ color: 'studio.text.primary', fontWeight: 600 }}
                >
                  {data?.blueprints.total ?? '—'}{' '}
                  {data?.blueprints.total === 1 ? 'blueprint' : 'blueprints'}
                </Box>{' '}
                {section === 'downloaded' ? 'on this computer' : 'to explore'}
              </>
            )}
          </Typography>
          <FormControlLabel
            sx={{ m: 0, gap: 0.75, '& .MuiTypography-root': { fontSize: 12 } }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={stockOnly}
                disabled={blueprintType === 'preset'}
                onChange={(e) => {
                  setStockOnly(e.target.checked);
                  setOffset(0);
                }}
              />
            }
            label="Stock devices only"
          />
        </Stack>
      </Box>
      {(error || actionError || library.error) && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              onClick={() => {
                setActionError('');
                library.setError('');
                reload();
              }}
            >
              Retry
            </Button>
          }
        >
          {error?.message || actionError || library.error}
        </Alert>
      )}
      <Box
        component="section"
        aria-label="Blueprint results"
        aria-busy={loading}
      >
        {loading && !data?.blueprints.items.length ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }, (_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                <Skeleton variant="rounded" height={330} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {data?.blueprints.items.map((blueprint, i) => (
              <Grid key={blueprint._id} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                <FadeContent
                  playOnMount
                  delay={Math.min(i, 6) * 40}
                  style={{ height: '100%' }}
                >
                  <SpotlightCard className="blueprint-card">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: 1, pb: 0, position: 'relative' }}>
                        <Box
                          component="button"
                          className="blueprint-art-button"
                          onClick={() =>
                            navigate(`/blueprints/${blueprint._id}`)
                          }
                          aria-label={`View ${blueprint.title}`}
                          sx={{
                            '& .blueprint-art': {
                              height: 146,
                              borderRadius: 2,
                            },
                          }}
                        >
                          <BlueprintArtwork
                            title={blueprint.title}
                            image={blueprint.thumbnailUrl}
                          />
                        </Box>
                        {library.downloaded[blueprint._id] && (
                          <Chip
                            size="small"
                            color="success"
                            label="Downloaded"
                            sx={{
                              position: 'absolute',
                              right: 16,
                              bottom: 8,
                              height: 22,
                              fontSize: 10,
                            }}
                          />
                        )}
                      </Box>
                      <Stack
                        spacing={1.25}
                        sx={{ p: 2, pb: 1.75, flex: 1, minWidth: 0 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: 10,
                              letterSpacing: '0.08em',
                              overflowWrap: 'anywhere',
                              textTransform: 'uppercase',
                            }}
                          >
                            {blueprint.blueprintType === 'preset'
                              ? `${blueprint.pluginName} ${blueprint.pluginVersion} · Preset`
                              : `Ableton Live ${blueprint.dawVersion} · FX chain`}
                          </Typography>
                          {blueprint.stockOnly && (
                            <Tooltip title="Uploader marked this blueprint as using stock devices only">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 10, whiteSpace: 'nowrap' }}
                              >
                                Stock devices
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                        <Box>
                          <Typography
                            component="button"
                            className="blueprint-title-button"
                            onClick={() =>
                              navigate(`/blueprints/${blueprint._id}`)
                            }
                            title={blueprint.title}
                            sx={{
                              color: 'text.primary',
                              fontSize: 17,
                              fontWeight: 600,
                              letterSpacing: '-0.015em',
                              lineHeight: 1.3,
                              mb: 0.75,
                            }}
                          >
                            {blueprint.title}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                          >
                            <Box
                              aria-hidden="true"
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: 'action.selected',
                                color: 'text.secondary',
                                fontSize: 10,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {Array.from(
                                blueprint.creatorName
                              )[0]?.toUpperCase() || '?'}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {creatorLink(blueprint)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="blueprint-description"
                          sx={{ fontSize: 12.5, lineHeight: '20px' }}
                        >
                          {blueprint.description ||
                            'A new effect chain for your next session.'}
                        </Typography>
                        <Stack
                          direction="row"
                          gap={0.75}
                          sx={{ minHeight: 24, overflow: 'hidden' }}
                        >
                          {blueprint.tags.slice(0, 3).map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              title={tag}
                              sx={{
                                height: 23,
                                fontSize: 10.5,
                                borderRadius: 1,
                                minWidth: 0,
                                '& .MuiChip-label': { px: 0.875 },
                              }}
                              onClick={() => {
                                setTags([tag]);
                                setOffset(0);
                              }}
                            />
                          ))}
                        </Stack>
                        <Box sx={{ mt: 'auto !important', pt: 1.25 }}>
                          {blueprint.previewUrl ? (
                            <BlueprintAudioPreview blueprint={blueprint} />
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ height: 38 }}
                            >
                              <GraphicEqIcon
                                color="disabled"
                                sx={{ fontSize: 16 }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: 11 }}
                              >
                                No audio preview
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                      <Box
                        sx={{
                          px: 2,
                          pt: 1.25,
                          pb: 1.5,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 10.5 }}
                          >
                            {blueprint.downloadCount.toLocaleString()}{' '}
                            {blueprint.downloadCount === 1
                              ? 'download'
                              : 'downloads'}
                          </Typography>
                          <Box
                            aria-hidden="true"
                            sx={{
                              width: 3,
                              height: 3,
                              borderRadius: '50%',
                              bgcolor: 'text.disabled',
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: 10.5 }}
                          >
                            {blueprint.favoriteCount.toLocaleString()}{' '}
                            {blueprint.favoriteCount === 1
                              ? 'favorite'
                              : 'favorites'}
                          </Typography>
                        </Stack>
                        {actions(blueprint, false, true)}
                      </Box>
                    </Box>
                  </SpotlightCard>
                </FadeContent>
              </Grid>
            ))}
          </Grid>
        )}
        {!loading &&
          !error &&
          !(localSection && (library.error || library.updateError)) &&
          data?.blueprints.total === 0 && (
            <Box sx={{ textAlign: 'center', py: 9 }}>
              <GraphicEqIcon
                sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
              />
              <Typography variant="h6">
                {section === 'outdated'
                  ? 'You’re up to date'
                  : section === 'downloaded'
                    ? 'Your next sound is waiting'
                    : section === 'favorites'
                      ? 'Keep your favorites close'
                      : hasFilters
                        ? 'No blueprints match this mix'
                        : 'Make the first connection'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                {hasFilters
                  ? 'Try another search or clear your filters.'
                  : section === 'outdated'
                    ? 'Your downloaded Blueprints have no available updates.'
                    : section === 'downloaded'
                      ? 'Download a blueprint to keep it here, ready to drag into your DAW.'
                      : section === 'favorites'
                        ? 'Tap the heart on a blueprint to find it here later.'
                        : 'Share a blueprint you love and help this library grow.'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  if (hasFilters) {
                    setSearch('');
                    setQuery('');
                    setTags([]);
                    setBlueprintType('');
                    setStockOnly(false);
                    setCreator(null);
                    setOffset(0);
                  } else if (section === 'explore' || section === 'mine')
                    openUpload();
                  else setSection('explore');
                }}
              >
                {hasFilters
                  ? 'Clear filters'
                  : section === 'explore' || section === 'mine'
                    ? 'Share a blueprint'
                    : 'Explore blueprints'}
              </Button>
            </Box>
          )}
        {!!data?.blueprints.total && (
          <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
            <Button
              disabled={offset === 0 || loading}
              onClick={() => setOffset((v) => Math.max(0, v - 24))}
            >
              Previous
            </Button>
            <Typography sx={{ alignSelf: 'center' }} variant="body2">
              {Math.floor(offset / 24) + 1} /{' '}
              {Math.max(1, Math.ceil(data.blueprints.total / 24))}
            </Typography>
            <Button
              disabled={offset + 24 >= data.blueprints.total || loading}
              onClick={() => setOffset((v) => v + 24)}
            >
              Next
            </Button>
          </Stack>
        )}
      </Box>
      {library.isElectron && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 4, color: 'text.secondary' }}
        >
          <FolderIcon fontSize="small" />
          <Typography variant="caption">
            Saved automatically to{' '}
            {library.folder || 'Documents/Studio Vault/Blueprints'}
          </Typography>
        </Stack>
      )}
      <BlueprintDownloadDialog library={library} />
    </Container>
  );
}
